import {
  assertObject,
  assertFunction,
  assertNumber,
  assertRequiredString,
  assertRequiredObject,
} from './utils';
import browse from './browse';
import request from './request';
import auth from './auth';
import user from './user';
import schedule from './schedule';
import subscription from './subscription';
import billing from './billing';
import geoip from './geoip';
import myList from './my-list';
import market from './market';
import playback from './playback';
import tracker from './tracker';
import parentalControl from './parental-control';

/** API client to access Magine public APIs. */
class API {
  /**
   * @param {Object} config - Key/value map of configuration.
   * @param {string} config.baseUri - Base URI of all API endpoints.
   * @param {string} config.clientApiToken - Client API token.
   * @param {Function} [config.fetch=global.fetch] - Fetch implementation
   * (like native window.fetch in browsers or 3rd party library)
   * @param [config.AbortController=global.AbortController] - AbortController implementation
   * @param {Object} [config.headers] - Key/value map of headers to be attached to each request.
   * @param {number} [config.requestTimeoutMs=15000] - request timeout in milliseconds
   *
   * @example
   * const api = new API({
   *   baseUri: 'https://example.tvoli.com/api',
   *   clientApiToken: '11111111-2222-3333-4444-555555555555',
   * });
   */
  constructor(config) {
    assertRequiredObject(config, 'config');
    assertRequiredString(config.baseUri, 'config.baseUri');
    assertRequiredString(config.clientApiToken, 'config.clientApiToken');
    assertFunction(config.fetch, 'config.fetch');
    assertNumber(config.requestTimeoutMs, 'config.requestTimeoutMs');
    assertObject(config.headers, 'config.headers');

    this.config = {
      fetch: global.fetch,
      AbortController: global.AbortController,
      requestTimeoutMs: 15000,
      ...config,
    };

    this.authData = null;

    this.auth = auth(this);
    this.browse = browse(this);
    this.user = user(this);
    this.schedule = schedule(this);
    this.subscription = subscription(this);
    this.billing = billing(this);
    this.geoip = geoip(this);
    this.myList = myList(this);
    this.market = market(this);
    this.playback = playback(this);
    this.tracker = tracker(this);
    this.parentalControl = parentalControl(this);
  }

  request(method, path, params = {}) {
    return request.call(this, method, path, params);
  }

  /**
   * Set authorization data. Data contains `token` and `userId`.
   *
   * @param {Object?} authData - Object with userId & token or null.
   * @param {string} authData.userId
   * @param {string} authData.token
   *
   * @return {this}
   */
  setAuthData(authData) {
    assertObject(authData, 'authData');
    if (authData) {
      assertRequiredString(authData.userId, 'authData.userId');
      assertRequiredString(authData.token, 'authData.token');
    }

    this.authData = authData;

    return this;
  }
}

export default API;
